import { CompanyRoleIdWithName } from "../common/commonDomain";
import { BoardMeetingDecisionVoter } from "./BoardMeetingMinutesForm";

export type Vote = "for" | "against" | "blank";

export type BoardMeetingVote = {
  companyroleId: number;
  decisionId: string;
  vote: Vote;
  votedAt: string;
};

export enum VotingMode {
  UNANIMOUS = "UNANIMOUS",
  VOTED_AGAINST = "VOTED_AGAINST",
  VOTING = "VOTING",
}

export type BoardMeetingVoting = {
  decisionId: string;
  isUnanimous: boolean;
  isApproved: boolean;
  membersVotedAgainst: CompanyRoleIdWithName[];
  mode?: VotingMode;
};

export function boardMeetingVoting(decisionId: string, isFreetext: boolean): BoardMeetingVoting {
  return {
    decisionId: decisionId,
    isUnanimous: !isFreetext,
    isApproved: !isFreetext,
    membersVotedAgainst: [],
  };
}

export type BoardMeetingVotingOutcome = Approved | NotApproved | QuorumNotReached;
export type Approved = { type: "approved"; reason: OutcomeReason };
export type NotApproved = { type: "notApproved"; reason: OutcomeReason };
export type QuorumNotReached = { type: "quorumNotReached"; reason: OutcomeReason };

export type OutcomeReason =
  | AllDecisionsUnanimouslyApproved
  | Unanimous
  | VotesMissing
  | MajorityVotedFor
  | MajorityVotedAgainst
  | MeetingChairCastingVote
  | MoreThanOneThirdMustVoteFor
  | RandomChoice
  | BoardQuorumNotAchieved
  | BoardQuorumNotAchievedForDecision;

export type AllDecisionsUnanimouslyApproved = { type: "allDecisionsUnanimouslyApproved" };
export type Unanimous = { type: "unanimous" };
export type VotesMissing = { type: "votesMissing" };
export type MajorityVotedFor = { type: "majorityVotedFor"; votedFor: number; votedAgainst: number };
export type MajorityVotedAgainst = {
  type: "majorityVotedAgainst";
  votedFor: number;
  votedAgainst: number;
};
export type MeetingChairCastingVote = {
  type: "meetingChairCastingVote";
  votedFor: number;
  votedAgainst: number;
};
export type MoreThanOneThirdMustVoteFor = {
  type: "moreThanOneThirdMustVoteFor";
  votedFor: number;
  numberOfBoardMembers: number;
};
export type RandomChoice = { type: "randomChoice"; votedFor: number; votedAgainst: number };
export type BoardQuorumNotAchieved = {
  type: "boardQuorumNotAchieved";
  presentBoardMembers: number;
  numberOfBoardMembers: number;
};
export type BoardQuorumNotAchievedForDecision = {
  type: "boardQuorumNotAchievedForDecision";
  eligibleBoardMembers: number;
  numberOfBoardMembers: number;
};

export const approved = (reason: OutcomeReason): Approved => ({ type: "approved", reason });
export const notApproved = (reason: OutcomeReason): NotApproved => ({
  type: "notApproved",
  reason,
});
export const quorumNotReached = (reason: OutcomeReason): QuorumNotReached => ({
  type: "quorumNotReached",
  reason,
});
export const allDecisionsUnanimouslyApproved = (): AllDecisionsUnanimouslyApproved => ({
  type: "allDecisionsUnanimouslyApproved",
});
export const unanimous = (): Unanimous => ({ type: "unanimous" });
export const votesMissing = (): VotesMissing => ({ type: "votesMissing" });
export const majorityVotedFor = (votedFor: number, votedAgainst: number): MajorityVotedFor => ({
  type: "majorityVotedFor",
  votedFor,
  votedAgainst,
});
export const majorityVotedAgainst = (
  votedFor: number,
  votedAgainst: number,
): MajorityVotedAgainst => ({
  type: "majorityVotedAgainst",
  votedFor,
  votedAgainst,
});
export const meetingChairCastingVote = (
  votedFor: number,
  votedAgainst: number,
): MeetingChairCastingVote => ({
  type: "meetingChairCastingVote",
  votedFor,
  votedAgainst,
});
export const moreThanOneThirdMustVoteFor = (
  votedFor: number,
  numberOfBoardMembers: number,
): MoreThanOneThirdMustVoteFor => ({
  type: "moreThanOneThirdMustVoteFor",
  votedFor,
  numberOfBoardMembers,
});
export const randomChoice = (votedFor: number, votedAgainst: number): RandomChoice => ({
  type: "randomChoice",
  votedFor,
  votedAgainst,
});
export const boardQuorumNotAchieved = (
  presentBoardMembers: number,
  numberOfBoardMembers: number,
): BoardQuorumNotAchieved => ({
  type: "boardQuorumNotAchieved",
  presentBoardMembers,
  numberOfBoardMembers,
});
export const boardQuorumNotAchievedForDecision = (
  eligibleBoardMembers: number,
  numberOfBoardMembers: number,
): BoardQuorumNotAchievedForDecision => ({
  type: "boardQuorumNotAchievedForDecision",
  eligibleBoardMembers,
  numberOfBoardMembers,
});

export function boardHasQuorum(presentBoardMembers: number, numberOfBoardMembers: number): boolean {
  return presentBoardMembers > numberOfBoardMembers / 2;
}

export function excludeIncompetentBoardMembers(
  presentBoardMembers: CompanyRoleIdWithName[],
  incompetentBoardMemberIds: number[],
): CompanyRoleIdWithName[] {
  return presentBoardMembers.filter((m) => !incompetentBoardMemberIds.includes(m.companyroleId));
}

export function votingOutcomeForDecision(
  {
    presentBoardMembers,
    incompetentBoardMemberIds,
    numberOfBoardMembers,
    meetingChair,
  }: {
    presentBoardMembers: CompanyRoleIdWithName[];
    incompetentBoardMemberIds: number[];
    numberOfBoardMembers: number;
    meetingChair: CompanyRoleIdWithName | undefined;
  },
  voting: BoardMeetingVoting | undefined,
  votesUsingVoting: BoardMeetingDecisionVoter[],
): BoardMeetingVotingOutcome {
  if (!boardHasQuorum(presentBoardMembers.length, numberOfBoardMembers)) {
    return quorumNotReached(
      boardQuorumNotAchieved(presentBoardMembers.length, numberOfBoardMembers),
    );
  }

  const eligibleBoardMembers = excludeIncompetentBoardMembers(
    presentBoardMembers,
    incompetentBoardMemberIds,
  );
  if (!boardHasQuorum(eligibleBoardMembers.length, numberOfBoardMembers)) {
    return quorumNotReached(
      boardQuorumNotAchievedForDecision(eligibleBoardMembers.length, numberOfBoardMembers),
    );
  }

  if (!voting) {
    return notApproved(votesMissing());
  }

  if (voting.mode !== undefined) {
    switch (voting.mode) {
      case VotingMode.UNANIMOUS:
        return approved(unanimous());
      case VotingMode.VOTED_AGAINST:
        return calculateVotingOutcome(
          { presentBoardMembers, incompetentBoardMemberIds, numberOfBoardMembers, meetingChair },
          voting,
        );
      case VotingMode.VOTING: {
        const votedNo = votesUsingVoting.filter((v) => v.vote === "against").length;
        if (votedNo === 0) {
          return approved(unanimous());
        }
        return calculateVotingOutcome(
          { presentBoardMembers, incompetentBoardMemberIds, numberOfBoardMembers, meetingChair },
          voting,
        );
      }
    }
  } else if (voting.isUnanimous) {
    return approved(unanimous());
  }
  return calculateVotingOutcome(
    { presentBoardMembers, incompetentBoardMemberIds, numberOfBoardMembers, meetingChair },
    voting,
  );
}

export function calculateVotingOutcome(
  {
    presentBoardMembers,
    incompetentBoardMemberIds,
    numberOfBoardMembers,
    meetingChair,
  }: {
    presentBoardMembers: CompanyRoleIdWithName[];
    incompetentBoardMemberIds: number[];
    numberOfBoardMembers: number;
    meetingChair: CompanyRoleIdWithName | undefined;
  },
  voting: BoardMeetingVoting,
): BoardMeetingVotingOutcome {
  const canVote = excludeIncompetentBoardMembers(
    presentBoardMembers,
    incompetentBoardMemberIds,
  ).length;
  const votedAgainst = voting.membersVotedAgainst.length;
  const votedFor = canVote - votedAgainst;

  if (votedFor <= numberOfBoardMembers / 3) {
    return notApproved(moreThanOneThirdMustVoteFor(votedFor, numberOfBoardMembers));
  }

  if (votedFor === votedAgainst) {
    const isMeetingChairIncompetent = incompetentBoardMemberIds.some(
      (companyroleId) => meetingChair?.companyroleId === companyroleId,
    );
    const isMeetingChairVotedAgainst = voting.membersVotedAgainst.some(
      (m) => meetingChair?.companyroleId === m.companyroleId,
    );

    if (isMeetingChairIncompetent) {
      return notApproved(randomChoice(votedFor, votedAgainst));
    }
    if (isMeetingChairVotedAgainst) {
      return notApproved(meetingChairCastingVote(votedFor, votedAgainst));
    }
    return approved(meetingChairCastingVote(votedFor, votedAgainst));
  }

  if (votedFor > votedAgainst) {
    return approved(majorityVotedFor(votedFor, votedAgainst));
  }
  return notApproved(majorityVotedAgainst(votedFor, votedAgainst));
}
